import { ArrowLeft, Camera, Pencil, Trash2 } from 'lucide-react';
import { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PresenceBadge } from '@/features/presence';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { countryName } from '@/lib/countries';
import {
  employeeStatusLabel,
  employeeStatusVariant,
  employmentTypeLabel,
} from '../constants';
import { CustomFieldsPanel } from '@/features/custom-fields';
import { EmployeeDetailTabs } from '../components/EmployeeDetailTabs';
import { useDeleteEmployee, useEmployee, useUploadEmployeePhoto } from '../queries';
import { employeeName } from '../types';

export function EmployeeDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();

  const { data: employee, isPending, isError, error } = useEmployee(publicId);
  const manager = useEmployee(employee?.manager ?? -1);
  const del = useDeleteEmployee();
  const uploadPhoto = useUploadEmployeePhoto(publicId);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleDelete() {
    if (!employee) return;
    await del.mutateAsync(employee.id);
    navigate('/hrm/employees');
  }

  async function handlePhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) await uploadPhoto.mutateAsync(file);
    if (fileRef.current) fileRef.current.value = '';
  }

  if (isPending) {
    return (
      <div className="max-w-7xl space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/employees')}>
          <ArrowLeft /> Back
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
  const subtitle = [employee.job_title, employee.department_name].filter(Boolean).join(' · ');

  return (
    <div className="max-w-7xl space-y-6">
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground -ml-2"
        onClick={() => navigate('/hrm/employees')}
      >
        <ArrowLeft /> Employees
      </Button>

      {/* Identity header */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadPhoto.isPending}
            className="group focus-visible:ring-ring relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            aria-label="Change photo"
          >
            <Avatar className="size-16">
              {employee.photo ? <AvatarImage src={employee.photo} alt="" /> : null}
              <AvatarFallback className="text-xl">
                {initials(employeeName(employee))}
              </AvatarFallback>
              <PresenceBadge user={employee.user} className="size-3.5" />
            </Avatar>
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
              <Camera className="size-5" />
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handlePhoto}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{employeeName(employee)}</h1>
              <Badge variant={employeeStatusVariant(employee.status)}>
                {employeeStatusLabel(employee.status)}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-0.5 text-sm">
              {subtitle || employmentTypeLabel(employee.employment_type)}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => fileRef.current?.click()}
              disabled={uploadPhoto.isPending}
            >
              <Camera /> {uploadPhoto.isPending ? 'Uploading…' : 'Change photo'}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/hrm/employees/${employee.public_id}/edit`)}
            >
              <Pencil /> Edit
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">
                  <Trash2 /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {employeeName(employee)}?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} disabled={del.isPending}>
                    {del.isPending ? 'Deleting…' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Email">{employee.email || '—'}</Field>
          <Field label="Personal email">{employee.personal_email || '—'}</Field>
          <Field label="Linked account">
            {employee.user_email ? (
              employee.user_email
            ) : (
              <span className="text-muted-foreground">Not linked</span>
            )}
          </Field>
          <Field label="Phone">{employee.phone_number || '—'}</Field>
          <Field label="Code">{employee.code || '—'}</Field>
          <Field label="Job title">{employee.job_title || '—'}</Field>
          <Field label="Department">{employee.department_name || '—'}</Field>
          <Field label="Type">{employmentTypeLabel(employee.employment_type)}</Field>
          <Field label="Manager">
            {employee.manager ? (manager.data ? employeeName(manager.data) : '…') : '—'}
          </Field>
          <Field label="City">{employee.city || '—'}</Field>
          <Field label="Country">{countryName(employee.country) || '—'}</Field>
          <Field label="Hire date">{fmtDate(employee.hire_date)}</Field>
          <Field label="Trial period end">{fmtDate(employee.trial_period_end)}</Field>
          <Field label="Address">
            <span className="whitespace-pre-line">{employee.address || '—'}</span>
          </Field>
          <Field label="End date">{fmtDate(employee.end_date)}</Field>
        </CardContent>
      </Card>

      {/* HR mirror of the employee's cabinet data. Salary stays masked
          server-side for roles outside SALARY_VISIBLE_ROLES (HR included). */}
      <Card>
        <CardContent>
          <EmployeeDetailTabs employeeId={employee.id} isSales={employee.is_sales} />
        </CardContent>
      </Card>

      <CustomFieldsPanel entity="hrm.employee" entityId={employee.id} />
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
